// @flow
import React, { Component } from 'react'
import { withRouter } from 'react-router-dom'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import { graphql } from 'react-apollo'
import gql from 'graphql-tag'
import moment from 'moment'
import Path from 'routes/path'
import SearchPage from 'components/pages/SearchPage'
import { viewerFragment } from 'common/fragments'
import {
  searchRoomsFragment,
  pageInfoFragment,
  ownerFragment,
} from 'common/fragments/www'
import type { Room } from 'types/www/room'
import type { PageInfo } from 'types/www/pageInfo'
import type { Amenity } from 'types/www/master'
import {
  queryStringParse,
  queryStringStringify,
  convertToSearchConditionType,
  convertToVariables,
} from 'helpers/search'
import { containerIds, setUIState } from 'redux/modules/ui'
import { actions as favoriteActions } from 'redux/modules/favorite'
import { actions as LocalRoomsActions } from 'redux/modules/local/rooms'
import {
  type SearchConditionType,
  setSearchCondition,
} from 'redux/modules/search'
import { ViewTypes } from 'frontend_core/dist/constants/search'
import { accountLoginUrl } from 'frontend_core/dist/helpers/url'
import { companyManagedFragment } from 'common/fragments'
import type { Room as RoomType } from 'types/room'

export const perPage = 20

export type PropTypes = {
  // apollo
  data: {
    search: {
      results: Array<Room>,
      pageInfo: PageInfo,
    },
    amenities: {
      results: Array<Amenity>,
    },
    sponsoredPromotions: {
      results: Array<{
        id: number,
        name: string,
      }>,
    },
    eventTypes: {
      results: Array<EventType>,
    },
    capacities: {
      results: Array<EventType>,
    },
    viewerData: Object,
    refetch: Function,
    loading: boolean,
  },
  loadRooms: (queries: SearchConditionType) => any,
  // react-router
  match: {
    params: {
      viewType: string,
    },
  },
  location: {
    pathname: string,
    search: string,
  },
  history: {
    push: Function,
    replace: Function,
  },
  // redux
  search: SearchConditionType,
  ui: {
    // マップ
    selectedRoom: ?Room,
    isShowRoomAnnotation: boolean,
    isShowReSearchButton: boolean,
    isShowMap: ?boolean,
    searchAtListView: ?string,
    // 一覧
    autoCompleteItems: Array<Object>,
    isOpenedAutocomplete: boolean,
    selectedPlaceId: ?string,
    showCheckInCalendar: boolean,
    showCapacity: boolean,
    showAmenityMore: boolean,
    showSearchForm: boolean,
    startedAt: ?moment,
    endedAt: ?moment,
    focusedInput: ?string,
    minCapacity: ?number,
    isBudgetUnspecified: boolean,
    searchCondition: SearchConditionType,
    // お気に入り
    room: RoomType,
    addedFavoriteRoomIds: Array<number>,
    deletedFavoriteRoomIds: Array<number>,
  },
  actions: {
    setUIState: Function,
    setSearchCondition: Function,
    onClickFavorite: Function,
    addFavorite: Function,
  },
  room: {
    roomDetail: {
      isFavorite: boolean,
      roomId: number,
    },
  },
  config: {
    SPM_GOOGLE_API_KEY: string,
    ORIGIN: string,
  },
  localRooms: {
    roomMap: Object,
  },
}

class Container extends Component<PropTypes> {
  constructor(props: PropTypes) {
    super(props)
    const { actions } = props

    this.onClickFavorite = this.onClickFavorite.bind(this)

    actions.setSearchCondition(
      convertToSearchConditionType(props.location.search),
    )
    actions.setUIState(containerIds.SEARCH, {
      showSearchForm: !props.config.IS_MOBILE,
      isShowMap: props.location.pathname === Path.searchMap(),
      isOpenFavoriteSelectModal: false,
      addedFavoriteRoomIds: [],
      deletedFavoriteRoomIds: [],
    })
  }

  componentDidMount() {
    const { data, actions } = this.props
    actions.importFromRoomList(data.search.results)
  }

  componentWillReceiveProps(nextProps: PropTypes) {
    const { search, ui, location, history, data, actions } = this.props
    const { search: nextSearch, ui: nextUI } = nextProps

    const currentLocationPage = queryStringParse(location.search).page

    // トータルページ数より大きいページ番号なら最後のページに移動(履歴追加なし)
    if (currentLocationPage > data.search.pageInfo.totalPages) {
      const query = queryStringParse(location.search)
      query.page = data.search.pageInfo.totalPages
      history.replace(`${location.pathname}?${queryStringStringify(query)}`)
      return
    }

    // 検索条件かマップ表示状態が変わっていたら、ViewTypeごとのURLとヒストリーを更新して、更新処理をかける
    if (
      JSON.stringify(search) !== JSON.stringify(nextSearch) ||
      ui.isShowMap !== nextUI.isShowMap
    ) {
      const pathname = nextUI.isShowMap ? Path.searchMap() : Path.searchB()
      history.push(`${pathname}${location.search}`)
      history.replace(`${pathname}?${queryStringStringify(nextSearch)}`)
      nextProps.data.refetch(convertToVariables(nextSearch))
      actions.importFromRoomList(data.search.results)
    }
  }

  onClickFavorite(
    e: Event,
    room: RoomType,
    isFavorite: boolean,
    favoriteListId?: string,
  ) {
    e.preventDefault()
    const { actions, config } = this.props
    const currentUser = this.props.data.viewer

    if (!currentUser) {
      window.location = accountLoginUrl(config)
    } else if (isFavorite) {
      // すでにお気に入りならお気に入りリストを開く
      actions.setUIState(containerIds.SEARCH, {
        isOpenFavoriteSelectModal: true,
        room,
      })
    } else {
      const { actions } = this.props
      actions.addFavorite({
        userId: currentUser.id,
        roomId: room.id,
        favoriteListId,
      })
    }
  }

  render() {
    const { data, location, search, ui, history, room, localRooms } = this.props

    // ビュータイプ
    const viewType =
      location.pathname === Path.searchMap() ? ViewTypes.Map : ViewTypes.List
    const isShowMap = viewType === ViewTypes.Map

    const pageInfo = data.search.pageInfo
    const searchSessionId = data.search.searchSessionId

    const rooms = data.search.results.map(room => ({
      ...room,
      isFavorite: (localRooms.roomMap[room.id] || {}).isFavorite,
    }))

    return (
      <SearchPage
        linkPrev={
          pageInfo.hasPreviousPage
            ? `${location.pathname}?${queryStringStringify(
                Object.assign({}, search, {
                  page: pageInfo.previousPage,
                  searchSessionId: searchSessionId,
                }),
              )}`
            : null
        }
        linkNext={
          pageInfo.hasNextPage
            ? `${location.pathname}?${queryStringStringify(
                Object.assign({}, search, {
                  page: pageInfo.nextPage,
                  searchSessionId: searchSessionId,
                }),
              )}`
            : null
        }
        onClickFavorite={this.onClickFavorite}
        linkCanonical={`${config.ORIGIN}${location.pathname}`}
        isShowMap={isShowMap}
        isLoading={data.loading}
        history={history}
        perPage={perPage}
        location={location}
        data={data}
        pageInfo={data.search.pageInfo}
        searchSessionId={searchSessionId}
        amenities={data.amenities.results}
        eventTypes={data.eventTypes.results}
        capacities={data.capacities.results}
        sponsoredPromotions={data.sponsoredPromotions.results}
        rooms={rooms}
        config={this.props.config}
        ui={ui}
        roomDetail={room.roomDetail}
      />
    )
  }
}

const searchQuery = gql`
query Container(
  $page: Int,
  $startedAt: String,
  $endedAt: String,
  $amenities: String,
  $geocode: String,
  $minCapacity: Int,
  $orderBy: RoomOrder,
  $hasDirectReservationPlans: Boolean,
  $hasLastMinuteDiscountPlans: Boolean,
  $ownerRank: OwnerRank,
  $minPrice: Int,
  $maxPrice: Int,
  $keyword: String,
  $state: String,
  $eventType: String,
  $sponsoredPromotionId: String,
  $withRecommend: Boolean,
) {
  search(
    page: $page,
    perPage: ${perPage}
    startedAt: $startedAt
    endedAt: $endedAt
    amenities: $amenities
    geocode: $geocode
    minCapacity: $minCapacity
    orderBy: $orderBy
    hasDirectReservationPlans: $hasDirectReservationPlans
    hasLastMinuteDiscountPlans: $hasLastMinuteDiscountPlans
    ownerRank: $ownerRank
    minPrice: $minPrice
    maxPrice: $maxPrice
    keyword: $keyword
    state: $state
    eventType: $eventType
    rentType: DAY_TIME
    sponsoredPromotionId: $sponsoredPromotionId
    withRecommend: $withRecommend
  ) {
    ${searchRoomsFragment}
    ${pageInfoFragment}
    searchSessionId
  }
  amenities(rentType: DAY_TIME) {
    results {
      name
      nameText
    }
  }
  eventTypes(rentType: DAY_TIME) {
    results {
      name
      nameText
    }
  }
  capacities {
    results {
      number
    }
  }
  sponsoredPromotions {
    results {
      id
      name
    }
  }
  viewer {
    ${viewerFragment}
    owners {
      results {
        ... on Owner {
          ${ownerFragment}
        }
      }
    }
    isCorp
    companies(filter: MANAGED) {
      results{
        ... on Company {
          ${companyManagedFragment}
        }
      }
    }
  }
}
`

const withData = graphql(searchQuery, {
  options: (props: PropTypes) => {
    const queries = convertToSearchConditionType(props.location.search)
    return {
      variables: convertToVariables(queries),
    }
  },
})(Container)

function mapStateToProps(state: Object) {
  return state
}
function mapDispatchToProps(dispatch: Dispatch<*>) {
  const bindActions = {
    setUIState,
    setSearchCondition,
    ...favoriteActions,
    ...LocalRoomsActions,
  }
  return { actions: bindActionCreators(bindActions, dispatch) }
}
export default withRouter(
  connect(
    mapStateToProps,
    mapDispatchToProps,
  )(withData),
)



// WEBPACK FOOTER //
// ./src/containers/pages/SearchPage/index.jsx